import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable } from "rxjs";
import { catchError, finalize, map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { historyServerApiColletions, socketServerApiColletions } from "src/app/core/constants/api_collection";

export interface ResponseModel {
  message: string;
  data: any;
  id?: any;
  /** Human-readable record number from the `identifier` header (e.g. "#1878"). */
  identifier?: string | null;
  /** Set when a booking still needs a card capture. */
  isPaymentRequired?: boolean;
  token?: any;
}

@Injectable({
  providedIn: "root"
})
export class ApiService {
  public apiLoadingSubject = new BehaviorSubject<boolean>(false);

  apiLoading$ = this.apiLoadingSubject.asObservable();

  constructor(private _http: HttpClient) {}

  startLoader(parameters: any): void {
    this.apiLoadingSubject.next(true);

    if (parameters?.isShowLoader === false) {
      this.apiLoadingSubject.next(false);
      delete parameters.isShowLoader;
    }
  }

  post({ url, parameters, apiHeaders = null }: { url: string; parameters: any; apiHeaders?: any }): Observable<ResponseModel> {
    this.startLoader(parameters);

    const headers = apiHeaders;

    const baseUrl: any = this.getBaseUrl(url);
    const callUrl: string = baseUrl + url;

    return this._http.post(callUrl, parameters, { headers: headers, observe: "response" }).pipe(
      map((responseData: any) => {
        if (responseData.headers?.get("id")) {
          return {
            data: responseData.body,
            message: responseData["message"],
            id: responseData.headers?.get("id"),
            // Human-readable record number (e.g. "#1878") — the panels show this
            // instead of the raw Mongo id from `id`.
            identifier: responseData.headers?.get("identifier")
          };
        } else if (responseData.headers?.get("authorization")) {
          return { data: responseData.body, message: responseData["message"], token: responseData.headers?.get("authorization") };
        }
        return { data: responseData.body, message: responseData["message"] };
      }),
      catchError((error) => {
        throw error;
      }),
      finalize(() => {
        setTimeout(() => {
          this.apiLoadingSubject.next(false);
        }, 500);
      })
    );
  }

  get({ url, parameters, apiHeaders = null }: { url: string; parameters?: any; apiHeaders?: any }): Observable<ResponseModel> {
    this.startLoader(parameters);

    const headers = apiHeaders;
    const baseUrl: any = this.getBaseUrl(url);
    const callUrl: string = baseUrl + url;

    return this._http.get(callUrl, { params: parameters, headers: headers, observe: "response" }).pipe(
      map((responseData: any) => {
        return { data: responseData.body, message: responseData["message"] };
      }),
      catchError((error) => {
        throw error;
      }),
      finalize(() => {
        setTimeout(() => {
          this.apiLoadingSubject.next(false);
        }, 500);
      })
    );
  }

  patch({ url, parameters, apiHeaders = null }: { url: string; parameters: any; apiHeaders?: any }): Observable<ResponseModel> {
    this.startLoader(parameters);

    const headers = apiHeaders;
    const baseUrl: any = this.getBaseUrl(url);
    const callUrl: string = baseUrl + url;

    return this._http.patch(callUrl, parameters, { headers: headers, observe: "response" }).pipe(
      map((responseData: any) => {
        return { data: responseData.body, message: responseData["message"] };
      }),
      catchError((error) => {
        throw error;
      }),
      finalize(() => {
        setTimeout(() => {
          this.apiLoadingSubject.next(false);
        }, 500);
      })
    );
  }

  put({ url, parameters, apiHeaders = null }: { url: string; parameters: any; apiHeaders?: any }): Observable<ResponseModel> {
    this.startLoader(parameters);

    const headers = apiHeaders;
    const baseUrl: any = this.getBaseUrl(url);
    const callUrl: string = baseUrl + url;

    return this._http.put(callUrl, parameters, { headers: headers, observe: "response" }).pipe(
      map((responseData: any) => {
        return { data: responseData.body, message: responseData["message"] };
      }),
      catchError((error) => {
        throw error;
      }),
      finalize(() => {
        setTimeout(() => {
          this.apiLoadingSubject.next(false);
        }, 500);
      })
    );
  }

  delete({ url, parameters, apiHeaders = null }: { url: string; parameters: any; apiHeaders?: any }): Observable<ResponseModel> {
    this.startLoader(parameters);

    const headers = apiHeaders;
    const baseUrl: any = this.getBaseUrl(url);
    const callUrl: string = baseUrl + url;

    return this._http.delete(callUrl, { headers: headers, body: parameters, observe: "response" }).pipe(
      map((responseData: any) => {
        return { data: responseData.body, message: responseData["message"] };
      }),
      catchError((error) => {
        throw error;
      }),
      finalize(() => {
        setTimeout(() => {
          this.apiLoadingSubject.next(false);
        }, 500);
      })
    );
  }

  getBaseUrl(url: any): string {
    if (Object.values(historyServerApiColletions).some((apiUrl) => url.startsWith(apiUrl))) {
      return environment.HISTORY_URL;
    } else if (Object.values(socketServerApiColletions).some((apiUrl) => url.startsWith(apiUrl))) {
      return environment.SOCKET_URL;
    }
    return environment.API_URL;
  }
}
